import {Card, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';

import {useCallback} from 'react';

import {
  useAddToAllSaleVehiclesMutation,
  useDeletePromotionalPhotoMutation,
  useRemoveInAllSaleVehiclesMutation,
  useResetPositionsInAllSaleVehiclesMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function PromoPhotosDataGrid() {
  const navigate = useNavigate();
  const [addToAllSaleVehicles] = useAddToAllSaleVehiclesMutation();
  const [resetPositionsInAllSaleVehicles] = useResetPositionsInAllSaleVehiclesMutation();
  const [removeInAllSaleVehicles] = useRemoveInAllSaleVehiclesMutation();
  const [deletePromotionalPhoto] = useDeletePromotionalPhotoMutation();

  const handleAddToAllVehicles = useCallback(
    (promotionalPhotoIds: string[], reload: () => void) => {
      addToAllSaleVehicles({
        addToAllSaleVehiclesRequestBody: {promotionalPhotoIds},
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('entity.promoPhotos.notifications.addToAllVehicle'));
          reload();
        })
        .catch(handleApiError);
    },
    [addToAllSaleVehicles]
  );

  const handleResetPositionsToAllVehicles = useCallback(
    (promotionalPhotoIds: string[], reload: () => void) => {
      resetPositionsInAllSaleVehicles({
        resetPositionsInAllSaleVehiclesRequestBody: {promotionalPhotoIds},
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.promoPhotos.notifications.resetPositionsToAllVehicles')
          );
          reload();
        })
        .catch(handleApiError);
    },
    [resetPositionsInAllSaleVehicles]
  );

  const handleRemoveInAllVehicles = useCallback(
    (promotionalPhotoIds: string[], reload: () => void) => {
      removeInAllSaleVehicles({
        removeInAllSaleVehiclesRequestBody: {promotionalPhotoIds},
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('entity.promoPhotos.notifications.removeInAllVehicles'));
          reload();
        })
        .catch(handleApiError);
    },
    [removeInAllSaleVehicles]
  );

  const handleDeletePromotionalPhoto = useCallback(
    (promotionalPhotoIds: string[], reload: () => void) => {
      deletePromotionalPhoto({
        deletePromotionalPhotoRequestBody: {promotionalPhotoIds},
      })
        .unwrap()
        .then(() => {
          showNotification.success(
            i18n.t('entity.promoPhotos.notifications.deletePromotionalPhoto')
          );
          reload();
        })
        .catch(handleApiError);
    },
    [deletePromotionalPhoto]
  );

  const actionCallback: ActionCallback = useCallback(
    ({actionKey, rowId, refreshData}) => {
      const promotionalPhotoIds = Array.isArray(rowId) ? rowId : [rowId];

      switch (actionKey) {
        case 'add_to_all_vehicles':
          handleAddToAllVehicles(promotionalPhotoIds, refreshData);

          break;
        case 'reset_positions_in_all_vehicles':
          handleResetPositionsToAllVehicles(promotionalPhotoIds, refreshData);

          break;
        case 'remove_from_all_vehicles':
          handleRemoveInAllVehicles(promotionalPhotoIds, refreshData);

          break;
        case 'delete':
          handleDeletePromotionalPhoto(promotionalPhotoIds, refreshData);

          break;
        case 'edit':
          navigate(
            composePath(settingsRoutes.advertisingPromoPhotosDetail, {
              params: {
                id: String(rowId),
              },
            })
          );

          break;
      }
    },
    [
      handleAddToAllVehicles,
      handleResetPositionsToAllVehicles,
      handleRemoveInAllVehicles,
      handleDeletePromotionalPhoto,
      navigate,
    ]
  );

  return (
    <Card
      actions={[
        {
          type: 'button',
          variant: 'link',
          onClick: () => navigate(settingsRoutes.advertisingPromoPhotosCreate),
          leftIcon: 'content/add',
          title: i18n.t('general.actions.addNew'),
        },
      ]}
      title={i18n.t('page.salesSettings.labels.promotionalPhotosList')}
    >
      <Box height={125}>
        <DmsDataGrid gridCode="promo-photo" actionCallback={actionCallback} />
      </Box>
    </Card>
  );
}
